import React, { useEffect, useState } from "react";
import { Drawer, Spin, Button, Typography, Divider, Grid, Flex } from "antd";
import { FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { ButtonType, ButtonShape } from "antd/lib/button/buttonHelpers";

interface LateralModalProps {
  children?: React.ReactElement;
  buttonName?: string;
  buttonSize?: "large" | "middle" | "small";
  buttonPosition?: "start" | "end";
  iconPosition?: "start" | "end";
  title?: string;
  titleIcon?: React.ReactNode;
  icon?: boolean;
  buttonType?: ButtonType;
  width?: string | number;
  maskClosable?: boolean;
  iconSvg?: React.ReactNode;
  buttonShape?: ButtonShape;
  strong?: boolean;
  openDrawer?: boolean;
  closeDrawer?: any;
  disabled?: boolean;
  noPadding?: boolean;
  // Oculta el botón-trigger interno: el drawer se controla 100% desde afuera
  // vía openDrawer/closeDrawer (para abrirlo desde otros elementos, ej. lápices).
  hideTrigger?: boolean;
}

export const LateralModal: React.FC<LateralModalProps> = ({
  children,
  buttonName,
  buttonSize = "middle",
  buttonPosition = "start",
  iconPosition = "start",
  title,
  titleIcon,
  icon = true,
  buttonType = "default",
  width = 480,
  maskClosable = true,
  iconSvg = <FilterOutlined />,
  buttonShape = "round",
  strong,
  openDrawer = false,
  disabled = false,
  closeDrawer,
  noPadding = false,
  hideTrigger = false,
}) => {
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { Title } = Typography;
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    setActiveDrawer(openDrawer);
  }, [openDrawer]);

  const closeModal = () => {
    setActiveDrawer(false);
    closeDrawer && closeDrawer();
  };

  const openModal = () => {
    setActiveDrawer(true);
  };

  if (isLoading) {
    return (
      <Drawer
        open
        styles={{
          body: {
            background: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Spin />
      </Drawer>
    );
  }

  return (
    <div>
      {!hideTrigger && (
        <Flex justify={buttonPosition}>
          <Button
            size={buttonSize}
            disabled={disabled}
            type={buttonType}
            shape={buttonShape}
            onClick={openModal}
            icon={icon ? iconSvg : null}
            iconPosition={iconPosition}
            style={{
              padding: noPadding ? 0 : "8px 16px",
            }}
          >
            {buttonName && (
              <span style={{ fontWeight: strong ? "600" : "400" }}>
                {buttonName}
              </span>
            )}
          </Button>
        </Flex>
      )}
      <Drawer
        destroyOnClose={true}
        onClose={closeModal}
        open={activeDrawer}
        width={screens.lg ? width : "90%"}
        placement="right"
        maskClosable={maskClosable}
        closable={true}
        styles={{
          body: {
            padding: 0,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          header: {
            display: "none",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: 56,
            paddingInlineStart: 24,
            paddingInlineEnd: 8,
            flexShrink: 0,
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {titleIcon ? titleIcon : null} {title ?? ""}
          </Title>
          <Button type="text" onClick={closeModal} style={{ fontSize: "20px" }}>
            <CloseOutlined />
          </Button>
        </div>
        <Divider style={{ margin: 0, flexShrink: 0 }} />
        {/* Sin padding a propósito: cada panel pone el suyo, `16px 24px`, porque muchos tienen un pie
            fijo con borde de lado a lado. Un panel que no lo pone queda pegado al borde (xcons-docs#509). */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children && React.cloneElement(children, { closeModal })}
        </div>
      </Drawer>
    </div>
  );
};
